import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import type { CanonicalWork, MediaAsset } from "../generated/contracts";
import { playerReducer, initialPlayerState } from "../player/state";
import { readLocalState, writeLocalState } from "../storage";
import { useLibrary } from "./LibraryContext";

type PlayerValue = {
  error: string;
  buffering: boolean;
  setQueue: (queue: CanonicalWork[]) => void;
  current: CanonicalWork | null;
  queue: CanonicalWork[];
  playing: boolean;
  currentTime: number;
  duration: number;
  rate: number;
  sleepAt: number | null;
  load: (
    work: CanonicalWork,
    queue?: CanonicalWork[],
    startSeconds?: number,
  ) => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  skip: (seconds: number) => void;
  setRate: (rate: number) => void;
  setSleepTimer: (minutes: number | null) => void;
  previous: () => void;
  next: () => void;
  playableAsset: (work: CanonicalWork) => MediaAsset | undefined;
};

const Context = createContext<PlayerValue | null>(null);
const playableAsset = (work: CanonicalWork) =>
  work.assets.find((asset) => asset.kind === "audio");

export function PlayerProvider({ children }: React.PropsWithChildren) {
  const { progress, recordProgress, markComplete, hydrated } = useLibrary();
  const [state, dispatch] = useReducer(playerReducer, initialPlayerState);
  const [storageReady, setStorageReady] = useState(false);
  const lastSaved = useRef(-5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const [buffering, setBuffering] = useState(false);
  const audio = useRef<HTMLAudioElement>(null);
  const pendingStart = useRef<number | null>(null);

  useEffect(() => {
    if (!hydrated) return;
    let active = true;
    readLocalState<{
      current: CanonicalWork | null;
      queue: CanonicalWork[];
      rate: number;
    }>("player", { current: null, queue: [], rate: 1 })
      .then((stored) => {
        if (!active) return;
        if (stored.current)
          dispatch({ type: "load", work: stored.current, queue: stored.queue });
        dispatch({ type: "pause" });
        dispatch({ type: "rate", rate: stored.rate || 1 });
        setStorageReady(true);
      })
      .catch(() => {
        if (active) setStorageReady(true);
      });
    return () => {
      active = false;
    };
  }, [hydrated]);
  useEffect(() => {
    if (!storageReady) return;
    writeLocalState("player", {
      current: state.current,
      queue: state.queue,
      rate: state.rate,
    }).catch(() => undefined);
  }, [state.current, state.queue, state.rate, storageReady]);

  useEffect(() => {
    const element = audio.current;
    if (!element || !state.current) return;
    const asset = playableAsset(state.current);
    if (!asset) return;
    if (element.src !== asset.url) {
      setError("");
      setCurrentTime(0);
      setDuration(0);
      lastSaved.current = -5;
      element.src = asset.url;
      element.load();
      pendingStart.current =
        pendingStart.current ?? progress[state.current.id] ?? 0;
    }
    element.playbackRate = state.rate;
    if (state.playing)
      element.play().catch(() => {
        dispatch({ type: "pause" });
        setError("Playback could not start. Tap Play to try again.");
      });
    else element.pause();
  }, [progress, state.current, state.playing, state.rate]);

  useEffect(() => {
    if (!("mediaSession" in navigator) || !state.current) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: state.current.title,
      artist:
        state.current.contributors.map((item) => item.name).join(", ") ||
        "Eternally Blessed Library",
      album: state.current.series?.title || "Eternally Blessed Library",
      artwork: state.current.artworkUrl
        ? [{ src: state.current.artworkUrl }]
        : [],
    });
    navigator.mediaSession.setActionHandler("play", () => {
      if (audio.current?.paused) dispatch({ type: "toggle" });
    });
    navigator.mediaSession.setActionHandler("pause", () =>
      dispatch({ type: "pause" }),
    );
    navigator.mediaSession.setActionHandler("seekbackward", (details) => {
      if (audio.current) audio.current.currentTime -= details.seekOffset || 15;
    });
    navigator.mediaSession.setActionHandler("seekforward", (details) => {
      if (audio.current) audio.current.currentTime += details.seekOffset || 15;
    });
    navigator.mediaSession.setActionHandler("previoustrack", () =>
      dispatch({ type: "advance", direction: -1 }),
    );
    navigator.mediaSession.setActionHandler("nexttrack", () =>
      dispatch({ type: "advance", direction: 1 }),
    );
  }, [state.current]);

  useEffect(() => {
    if (!state.sleepAt) return;
    const timer = window.setInterval(() => {
      if (Date.now() >= state.sleepAt!) {
        dispatch({ type: "pause" });
        dispatch({ type: "sleep", sleepAt: null });
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [state.sleepAt]);

  const load = useCallback(
    (work: CanonicalWork, queue?: CanonicalWork[], startSeconds?: number) => {
      setError("");
      pendingStart.current = startSeconds ?? progress[work.id] ?? 0;
      if (state.current?.id === work.id && audio.current) {
        audio.current.currentTime = pendingStart.current;
        pendingStart.current = null;
      }
      dispatch({ type: "load", work, queue });
    },
    [progress, state.current?.id],
  );
  const seek = useCallback((seconds: number) => {
    if (audio.current)
      audio.current.currentTime = Math.max(
        0,
        Math.min(audio.current.duration || seconds, seconds),
      );
  }, []);
  const skip = useCallback(
    (seconds: number) => {
      if (audio.current) seek(audio.current.currentTime + seconds);
    },
    [seek],
  );
  const setRate = useCallback(
    (rate: number) => dispatch({ type: "rate", rate }),
    [],
  );
  const setSleepTimer = useCallback(
    (minutes: number | null) =>
      dispatch({
        type: "sleep",
        sleepAt: minutes ? Date.now() + minutes * 60_000 : null,
      }),
    [],
  );
  const value = useMemo<PlayerValue>(
    () => ({
      error,
      buffering,
      setQueue: (queue) => dispatch({ type: "queue", queue }),
      current: state.current,
      queue: state.queue,
      playing: state.playing,
      currentTime,
      duration,
      rate: state.rate,
      sleepAt: state.sleepAt,
      load,
      toggle: () => dispatch({ type: "toggle" }),
      seek,
      skip,
      setRate,
      setSleepTimer,
      previous: () => dispatch({ type: "advance", direction: -1 }),
      next: () => dispatch({ type: "advance", direction: 1 }),
      playableAsset,
    }),
    [
      state,
      error,
      buffering,
      currentTime,
      duration,
      load,
      seek,
      skip,
      setRate,
      setSleepTimer,
    ],
  );

  return (
    <Context.Provider value={value}>
      {children}
      <audio
        ref={audio}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => {
          setBuffering(false);
          setError("");
        }}
        onCanPlay={() => setBuffering(false)}
        onError={() => {
          setBuffering(false);
          setError(
            "This recording is unavailable. Try another source on its page.",
          );
          dispatch({ type: "pause" });
        }}
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration || 0);
          if (pendingStart.current != null) {
            event.currentTarget.currentTime = Math.min(
              pendingStart.current,
              event.currentTarget.duration || pendingStart.current,
            );
            pendingStart.current = null;
          }
        }}
        onPause={(event) => {
          if (state.current && event.currentTarget.currentTime > 0)
            recordProgress(state.current, event.currentTarget.currentTime);
        }}
        onTimeUpdate={(event) => {
          const seconds = event.currentTarget.currentTime;
          setCurrentTime(seconds);
          if (state.current && Math.abs(seconds - lastSaved.current) >= 5) {
            lastSaved.current = seconds;
            recordProgress(state.current, seconds);
          }
        }}
        onEnded={() => {
          if (state.current) markComplete(state.current);
          dispatch({ type: "advance", direction: 1 });
        }}
      />
    </Context.Provider>
  );
}

export function usePlayer() {
  const value = useContext(Context);
  if (!value) throw new Error("usePlayer must be inside PlayerProvider");
  return value;
}
